"use client"

import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useSectionGate } from "@/components/layout/section-visibility-provider"

/**
 * Cross-page CTA pointing users to /insurance from contextual moments
 * (test-detail hero, pricing page, how-it-works). CH-only — renders nothing
 * for other countries.
 *
 * Sizes:
 *   - "lg" (default): filled neutral-50 pill with a thin neutral-200 ring,
 *     white-circle icon backdrop. type-body-md text. Use under the trust
 *     pills on test heroes and primary content blocks.
 *   - "md": identical geometry to lg (same padding, text size, icon, white
 *     circle) but ring-less — sits on background fills without an outline.
 *   - "sm": outline pill — full-width on mobile (centered group), content-
 *     hugged on md+. type-body-sm text, plain icon by default.
 *
 * Props:
 *   - `icon` (default "heart"):
 *       - `"heart"` — person+heart, the heart pulses on hover.
 *       - `"heart-check"` — heart-with-check; on hover the check "draws"
 *         in left-to-right while scaling 1.4×.
 *       - `"heart-check-pulse"` — heart-with-check; the check stays
 *         static and the heart pulses on hover (same keyframe as
 *         `"heart"`).
 *     All icons render in neutral-900 (or white/90 on dark surfaces).
 *   - `pulse` (default true) — heart pulses on hover (heart icon only);
 *     pass false to disable.
 *   - `circle` — icon backdrop. Defaults to true at "lg", false at "md" / "sm".
 *   - `dark` — for placement on dark surfaces (mobile test-detail hero).
 *   - `className` — appended; useful for max-width caps in callers.
 */
type Size = "lg" | "md" | "sm"
type IconChoice = "heart" | "heart-check" | "heart-check-pulse"

export function InsuranceCoverageCta({
  size = "lg",
  pulse = true,
  circle,
  dark,
  icon = "heart",
  className,
}: {
  size?: Size
  pulse?: boolean
  circle?: boolean
  dark?: boolean
  icon?: IconChoice
  className?: string
}) {
  const lp = useLocalePath()
  const { code: country } = useCountry()
  const isAllowed = useSectionGate()
  const t = useTranslations("ui")

  if (!isAllowed("section:marketing.insurance-coverage-cta", country)) {
    return null
  }

  const showCircle = circle ?? size === "lg"

  // Per-size geometry. sm collapses to a centered, full-width row on
  // mobile (< md breakpoint) and resolves to a content-hugged pill at
  // md+. lg / md always hug content. On dark surfaces (mobile hero)
  // every size hugs content + centers, since dark is mobile-only.
  const layout =
    size === "lg" || size === "md"
      ? {
          // lg / md hug their content at every viewport — they're not
          // meant to span as a row CTA. Use sm if you want full-width.
          base: "w-fit gap-2 px-4 py-2",
          textAlign: "flex-1 text-left",
          text: "type-body-md",
          icon: 22,
        }
      : {
          base: dark
            ? "w-fit gap-2.5 px-2 py-2"
            : "w-full justify-center gap-2.5 px-2 py-2 md:w-fit md:justify-start",
          textAlign: dark
            ? "text-center"
            : "text-center md:flex-1 md:text-left",
          text: "type-body-sm",
          icon: 22,
        }

  // Surface treatment: filled with ring (lg), no fill / no ring (md —
  // reads more like an inline link, including on dark), outline pill
  // (sm). Dark inverts the colors but keeps the same per-size structure.
  const surface =
    size === "md"
      ? dark
        ? "bg-transparent hover:bg-white/[0.06]"
        : "bg-transparent hover:bg-neutral-50"
      : dark
        ? "ring-1 ring-white/25 hover:bg-white/[0.06] hover:ring-white/40"
        : size === "lg"
          ? "bg-neutral-50 ring-1 ring-neutral-200 hover:bg-neutral-100 hover:ring-neutral-300"
          : "bg-transparent ring-1 ring-neutral-200 hover:bg-neutral-900/[0.03] hover:ring-neutral-300"

  return (
    <Link
      href={lp("/insurance")}
      className={cn(
        "group flex items-center rounded-full transition-colors duration-200",
        layout.base,
        surface,
        className
      )}
    >
      {showCircle ? (
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-neutral-900"
        >
          {renderIcon({ size: layout.icon, icon, pulse, dark })}
        </span>
      ) : (
        renderIcon({
          size: layout.icon,
          icon,
          pulse,
          dark,
          className: cn(
            "shrink-0",
            dark ? "text-white/90" : "text-neutral-900"
          ),
        })
      )}
      <span
        className={cn(
          "min-w-0 truncate font-medium",
          // leading-none collapses the line box to font-size, which clips
          // an underline (truncate adds overflow:hidden). md needs the
          // default line-height so the underline has room.
          size !== "md" && "leading-none",
          layout.textAlign,
          layout.text,
          dark ? "text-white/70" : "text-neutral-500",
          // md reads as an inline link — underline it. Underline sits at
          // 55% alpha so it reads as a soft hairline, not a hard rule.
          // Smoothly transition the decoration color on hover.
          size === "md" &&
            "underline decoration-neutral-300/55 underline-offset-4 transition-[text-decoration-color] duration-200 group-hover:decoration-neutral-500"
        )}
      >
        {t("checkInsuranceCoverage")}
      </span>
    </Link>
  )
}

/* Dispatch helper — picks the right icon component based on `icon` prop.
 * `dark` is treated as a "mobile placement" signal: all hover animations
 * are suppressed on dark since touch devices have sticky :hover and the
 * motion reads as broken when it stays mid-animation. */
function renderIcon({
  size,
  icon,
  pulse,
  dark,
  className,
}: {
  size: number
  icon: IconChoice
  pulse: boolean
  dark?: boolean
  className?: string
}) {
  const animate = !dark
  if (icon === "heart-check") {
    return (
      <HeartCheckDrawingIcon
        size={size}
        animate={animate}
        className={className}
      />
    )
  }
  if (icon === "heart-check-pulse") {
    return (
      <HeartCheckPulsingIcon
        size={size}
        pulse={pulse && animate}
        className={className}
      />
    )
  }
  return (
    <PersonHeartPulsingIcon
      size={size}
      pulse={pulse && animate}
      className={className}
    />
  )
}

/* Inline person+heart icon with the heart subpath split out so it can
 * pulse on its own. Geometry matches `PersonHeartIcon` in @/components/ui/icons.
 * The heart is centered around (17.7, 16); transform-box: fill-box plus
 * transform-origin: center makes the scale animation pivot from the heart's
 * own center rather than the SVG origin. Animation only fires on the parent
 * link's hover (group-hover) and only when `pulse` is true. */
function PersonHeartPulsingIcon({
  size = 24,
  pulse = true,
  className,
}: {
  size?: number
  pulse?: boolean
  className?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      {/* Person figure (body + head) */}
      <path d="M4.5 19.3078V17.0845C4.5 16.5948 4.633 16.1413 4.899 15.724C5.165 15.3067 5.5205 14.9858 5.9655 14.7615C6.93467 14.2897 7.91092 13.9327 8.89425 13.6905C9.87758 13.4482 10.8712 13.3206 11.875 13.3078C11.9097 13.3398 11.9423 13.3686 11.973 13.3942C12.0038 13.4199 12.0398 13.4488 12.0808 13.4808C11.9679 13.6811 11.8833 13.8952 11.827 14.1232C11.7705 14.3514 11.7422 14.5796 11.7422 14.8078C10.8679 14.8308 10.0042 14.9499 9.151 15.1652C8.29783 15.3807 7.46542 15.6923 6.65375 16.1C6.45258 16.209 6.29333 16.3497 6.176 16.522C6.05867 16.6945 6 16.882 6 17.0845V17.8078H13.123L14.623 19.3078H4.5Z" />
      <path d="M12 11.6922C11.0375 11.6922 10.2136 11.3496 9.52825 10.6642C8.84275 9.97875 8.5 9.15475 8.5 8.19225C8.5 7.22975 8.84275 6.40583 9.52825 5.7205C10.2136 5.035 11.0375 4.69225 12 4.69225C12.9625 4.69225 13.7864 5.035 14.4718 5.7205C15.1573 6.40583 15.5 7.22975 15.5 8.19225C15.5 9.15475 15.1573 9.97875 14.4718 10.6642C13.7864 11.3496 12.9625 11.6922 12 11.6922ZM12 10.1922C12.55 10.1922 13.0208 9.99642 13.4125 9.60475C13.8042 9.21308 14 8.74225 14 8.19225C14 7.64225 13.8042 7.17142 13.4125 6.77975C13.0208 6.38808 12.55 6.19225 12 6.19225C11.45 6.19225 10.9792 6.38808 10.5875 6.77975C10.1958 7.17142 10 7.64225 10 8.19225C10 8.74225 10.1958 9.21308 10.5875 9.60475C10.9792 9.99642 11.45 10.1922 12 10.1922Z" />
      {/* Heart — pulses only while the parent button is hovered (and only
          if `pulse` is true). Cycles back to scale(1) so a mouseleave
          mid-cycle lands cleanly. transform-box + origin pivot from the
          heart's own center (≈17.7, 16). */}
      <path
        d="M17.7172 19.3078L14.4865 16.077C14.2827 15.873 14.1397 15.6567 14.0577 15.4282C13.9757 15.1996 13.9348 14.9709 13.9348 14.7422C13.9348 14.2489 14.1111 13.8096 14.4637 13.4243C14.8166 13.0389 15.2691 12.8463 15.8212 12.8463C16.2549 12.8463 16.5957 12.9465 16.8435 13.147C17.0913 13.3477 17.3826 13.6153 17.7172 13.95C18.0314 13.6358 18.3153 13.3733 18.569 13.1625C18.8225 12.9517 19.1722 12.8463 19.618 12.8463C20.1867 12.8463 20.6425 13.0411 20.9855 13.4308C21.3285 13.8204 21.5 14.262 21.5 14.7555C21.5 14.9867 21.4548 15.2179 21.3645 15.4492C21.274 15.6804 21.1352 15.8897 20.948 16.077L17.7172 19.3078Z"
        style={{
          transformBox: "fill-box",
          transformOrigin: "center",
        }}
        className={cn(
          pulse &&
            "group-hover:[animation:heart-soft-pulse_1s_ease-in-out_infinite]",
          "motion-reduce:group-hover:[animation:none]"
        )}
      />
    </svg>
  )
}

/* Heart-with-check icon (Material Symbols geometry, viewBox 0 -960 960 960).
 *
 * The check is drawn as a stroked polyline V (rather than the original
 * filled badge) so we can use the classic SVG `stroke-dasharray` drawing
 * technique: with `pathLength="1"` and `stroke-dasharray: 1`, animating
 * stroke-dashoffset from 1 → 0 traces the line from the top-left tip,
 * down to the bottom point, and up to the top-right tip — unmistakably
 * "drawing" left-to-right.
 *
 * On hover the redraw animation plays first (~0.55s); the 2× scale
 * transition is delayed so it kicks in just as the redraw is wrapping
 * up. The `transition-delay` is scoped to `group-hover:` so on
 * mouseleave the scale-down is immediate. */
function HeartCheckDrawingIcon({
  size = 24,
  animate = true,
  className,
}: {
  size?: number
  animate?: boolean
  className?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 -960 960 960"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      // overflow:visible so the check can scale 2x past the viewBox edge
      // without being clipped by the SVG's CSS box.
      overflow="visible"
      className={className}
    >
      {/* Heart outline (always visible) */}
      <path d="M440-120 313-234q-72-65-123.5-116t-85-96q-33.5-45-49-87T40-621q0-94 63-156.5T260-840q52 0 99 22t81 62q34-40 81-62t99-22q81 0 136 45.5T831-680h-85q-18-40-53-60t-73-20q-51 0-88 27.5T463-660h-46q-31-45-70.5-72.5T260-760q-57 0-98.5 39.5T120-621q0 33 14 67t50 78.5q36 44.5 98 104T440-228q26-23 61-53t56-50l9 9 19.5 19.5L605-283l9 9q-22 20-56 49.5T498-172l-58 52Z" />

      {/* Check — stroked V over the centerline of the original Material
          filled check. Visible at rest (stroke-dashoffset 0).
          On hover-in: scale jumps to 2× instantly (transition: 0s), then
          the `check-draw` keyframe redraws the check at 2× size.
          On hover-out: the base transition (0.4s) reasserts itself, so
          the check smoothly scales back down to its original size. The
          asymmetric timing comes from overriding `transition` only inside
          the `group-hover:` rule. */}
      <path
        d="M632 -454 L718 -370 L888 -539"
        pathLength="1"
        fill="none"
        stroke="currentColor"
        strokeWidth="80"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          "[transform-origin:center] [transform:translate(0,-10px)_scale(1)] [stroke-dasharray:1] [transform-box:fill-box] [transition:transform_0.4s_cubic-bezier(0.65,0,0.35,1)] motion-reduce:[transform:translate(0,-10px)_scale(1)] motion-reduce:[animation:none] motion-reduce:[transition:none]",
          // Hover-triggered redraw + scale only fires when `animate` is on.
          // Disabled on mobile (dark) where touch :hover is sticky.
          animate &&
            "group-hover:[transform:translate(0,-11px)_scale(1.4)] group-hover:[animation:check-draw_0.55s_cubic-bezier(0.65,0,0.35,1)] group-hover:[transition:transform_0s]"
        )}
      />
    </svg>
  )
}

/* Heart-with-check icon — same geometry as HeartCheckDrawingIcon, but the
 * heart pulses on hover (matching `PersonHeartPulsingIcon`'s behavior) and
 * the check stays static. Use this when you want the affordance signal of
 * the heart variant but with the heart-check visual. */
export function HeartCheckPulsingIcon({
  size = 24,
  pulse = true,
  className,
}: {
  size?: number
  pulse?: boolean
  className?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 -960 960 960"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      // overflow:visible so the heart doesn't get clipped by the viewBox
      // edges when it pulses past its 1× bounding box.
      overflow="visible"
      className={className}
    >
      {/* Heart — pulses on hover when `pulse` is true. transform-box +
          origin center pivots the scale around the heart's own center. */}
      <path
        d="M440-120 313-234q-72-65-123.5-116t-85-96q-33.5-45-49-87T40-621q0-94 63-156.5T260-840q52 0 99 22t81 62q34-40 81-62t99-22q81 0 136 45.5T831-680h-85q-18-40-53-60t-73-20q-51 0-88 27.5T463-660h-46q-31-45-70.5-72.5T260-760q-57 0-98.5 39.5T120-621q0 33 14 67t50 78.5q36 44.5 98 104T440-228q26-23 61-53t56-50l9 9 19.5 19.5L605-283l9 9q-22 20-56 49.5T498-172l-58 52Z"
        style={{
          transformBox: "fill-box",
          transformOrigin: "center",
        }}
        className={cn(
          pulse &&
            "group-hover:[animation:heart-soft-pulse-sm_1s_ease-in-out_infinite]",
          "motion-reduce:group-hover:[animation:none]"
        )}
      />
      {/* Check — static stroked V (no animation) shifted up 10px to match
          HeartCheckDrawingIcon's resting position. */}
      <path
        d="M632 -454 L718 -370 L888 -539"
        pathLength="1"
        fill="none"
        stroke="currentColor"
        strokeWidth="80"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="[transform:translate(0,-10px)]"
      />
    </svg>
  )
}
